import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import axios from 'axios';
import { IconButton } from '../icon-button';
import { LoaderIcon, SendIcon } from '../fun-ui/icons';
import toast from 'react-hot-toast';

export type UploadPaperFromURLHandle = {
  fetchFileAndUploadFromURl: (url: string) => void;
}
type UploadPaperFromURLProps = {
  onFileFetched: (files: File[]) => Promise<void>;
}
export const UploadPaperFromURL = React.forwardRef<UploadPaperFromURLHandle, UploadPaperFromURLProps>(function UploadPaperFromURL({
  onFileFetched = async () => { },
}, ref) {
  const [url, setPdfUrl] = useState<string>('');
  const [fetching, setFetching] = useState<boolean>(false);

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setPdfUrl(event.target.value);
  };

  const handleUpload = useCallback(async (url: string) => {
    setFetching(true);
    const toastId = "toast-pdf-fetching";
    toast.loading('PDF 资源获取中，请稍等...', {
      id: toastId,
    });
    const files = await checkURLAndFetchResoures(completeURL(url));
    if (typeof files === 'string') {
      toast.error(files);
    } else {
      await onFileFetched(files);
    }
    toast.dismiss(toastId);
    setFetching(false);
  }, [onFileFetched]);

  useImperativeHandle(ref, () => {
    return {
      fetchFileAndUploadFromURl(url) {
        setPdfUrl(url);
        handleUpload(url);
      },
    };
  }, [handleUpload, setPdfUrl]);

  return (
    <div
      className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background flex-wrap justify-end"
    >
      <input
        className="flex-1 bg-background outline-none"
        type="text"
        value={url}
        onChange={handleUrlChange}
        placeholder="输入 PDF 文献网址链接快速上传"
      />
      <div className="flex-initial self-end flex justify-end -mt-1/2">
        {
          fetching ? (
            <IconButton
              label="提交"
              variant="default"
              disabled={fetching || !url}
              className="w-[30px] h-[30px] disabled:opacity-100 disabled:bg-border"
            >
              <LoaderIcon size={20} />
            </IconButton>
          ) : (
            <IconButton
              label="提交"
              variant="default"
              disabled={fetching || !url}
              onClick={() => handleUpload(url)}
              className="w-[30px] h-[30px] disabled:opacity-100 disabled:bg-border"
            >
              <SendIcon size={30} />
            </IconButton>
          )
        }
      </div>
    </div >
  );
});


export const isValidUrl = (urlstr: string): boolean => {
  try {
    // 使用URL对象验证url的合法性
    const url: URL = new URL(urlstr);
    // 如果URL对象的协议不是http或https，则抛出错误
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
};
export async function checkURLAndFetchResoures(
  url: string,
): Promise<File[] | string> {
  if (!isValidUrl(url)) {
    return 'PDF URL 地址格式错误';
  }

  try {
    const response = await axios.get(url, { responseType: 'arraybuffer' });
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('application/pdf')) {
      let fileName = url.substring(url.lastIndexOf('/') + 1);
      if (!fileName.endsWith('.pdf')) {
        fileName += '.pdf';
      }
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfFile = new File([pdfBlob], fileName, {
        type: pdfBlob.type,
      });
      return [pdfFile]; // 返回一个数组，以符合 Promise<File[] | string> 的定义
    } else {
      return 'PDF URL 地址格式错误';
    }
  } catch (fetchError) {
    console.error('Error fetching PDF:', fetchError);
    return 'PDF 获取失败，请检查 PDF URL 地址';
  } finally {
  }
}

export function completeURL(url: string): string {
  // 使用正则表达式检查url是否缺少https协议头
  const hasHttpProtocol: boolean = /^https?:\/\//.test(url);

  // 如果缺少https协议头，则补全url
  if (!hasHttpProtocol) {
    url = `https://${url}`;
  }
  return convertToPDFURL(url);
}

export function convertToPDFURL(url: string): string {
  // 使用正则表达式检查url是否包含arXiv.org并且是否是arXiv.org的链接
  const isArxivURL: boolean = /arxiv\.org\/(abs|pdf)\//.test(url);

  if (isArxivURL) {
    // 如果url包含arXiv.org，并且是arXiv.org的链接，则将其转换为PDF格式的url
    url = url.replace(/\/(abs|pdf)\//, '/pdf/');
    // url = url.replace(/\.pdf$/, '');
    // url = url + '.pdf';
    return url;
  } else {
    // 如果url不包含arXiv.org，则直接返回原始url
    return url;
  }
}